import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_CONFIG} from "@common/services/logging"
import {ArContentModel, getArAssignmentKey} from "@app/common/helpers/ar/ar"
import {isAndroid, isIoS} from "@cm/browser-utils"
import {triggerDOMLink} from "@app/common/helpers/routes"
import {Parameters} from "@cm/template-nodes"
import {assembleArUrl, generateAndroidLink} from "@cm/utils/ar-url"
import {
    ConfiguratorArDataGQL,
    ConfiguratorGenerateArGQL,
    ConfiguratorJobTaskForConfiguratorGQL,
} from "@app/common/components/viewers/configurator/configurator/configurator.generated"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {EndpointUrls} from "@common/models/constants"
import {JobState} from "@generated"
import {filter, firstValueFrom, map, take, Subject} from "rxjs"

type ArDataObject = {id: string; downloadUrl: string; objectName: string; mediaType?: string | null}

@Injectable({
    providedIn: "root",
})
export class ArService {
    //Events
    private desktopArUrl: Subject<string | undefined> = new Subject()
    desktopArUrl$ = this.desktopArUrl.asObservable()

    private creatingArSubject = new Subject<boolean>()
    creatingAr$ = this.creatingArSubject.asObservable()

    private readonly configuratorArDataGql = inject(ConfiguratorArDataGQL)
    private readonly configuratorGenerateArGql = inject(ConfiguratorGenerateArGQL)
    private readonly getJobTaskGql = inject(ConfiguratorJobTaskForConfiguratorGQL)
    private readonly logger: CmLogger = inject(CM_LOGGER_CONFIG)

    private async getExistingArModelDataObject(templateRevisionId: string, assignmentKey: string, contentType: ArContentModel): Promise<ArDataObject | null> {
        const {configuratorArData} = await fetchThrowingErrors(this.configuratorArDataGql)({
            templateRevisionId,
            assignmentKey,
        })

        if (!configuratorArData) return null

        switch (contentType) {
            case "model/gltf-binary":
                return configuratorArData.gltfDataObject ?? null
            case "model/vnd.usdz+zip":
                return configuratorArData.usdzDataObject ?? null
            default:
                throw Error(`Unknown type: ${contentType}.`)
        }
    }

    private async getArModelDataObject(templateRevisionId: string, parameters: Parameters, contentType: ArContentModel): Promise<ArDataObject> {
        const assignmentKey = getArAssignmentKey(parameters)

        let existingArModel = await this.getExistingArModelDataObject(templateRevisionId, assignmentKey, contentType)
        if (existingArModel) return existingArModel

        await this.generateNewArModels(templateRevisionId, parameters, assignmentKey)

        existingArModel = await this.getExistingArModelDataObject(templateRevisionId, assignmentKey, contentType)
        if (!existingArModel) throw new Error("AR generation failed")

        return existingArModel
    }

    private async generateNewArModels(templateRevisionId: string, parameters: Parameters, assignmentKey: string) {
        const templateParametersBase64 = btoa(JSON.stringify(parameters.serialize()))

        this.logger.debug(`Starting AR generation job for revision ${templateRevisionId}`)
        this.logger.debug("URL parameters:", `?templateRevisionId=${templateRevisionId}&templateParametersBase64=${templateParametersBase64}`)

        const {configuratorGenerateAr} = await mutateThrowingErrors(this.configuratorGenerateArGql)({
            input: {
                templateRevisionId,
                templateParametersBase64,
                assignmentKey,
            },
        })

        await this.waitForJobToFinish(configuratorGenerateAr.id)
    }

    private async waitForJobToFinish(jobId: string) {
        const jobTask = await firstValueFrom(
            this.getJobTaskGql.watch({id: jobId}, {pollInterval: 2000}).valueChanges.pipe(
                map(({data: {configuratorJobTask: jobTask}}) => jobTask),
                filter((jobTask) => {
                    if (!jobTask) return true
                    switch (jobTask.state) {
                        case JobState.Runnable:
                        case JobState.Running:
                        case JobState.Init:
                            return false
                        default:
                            return true
                    }
                }),
                take(1),
            ),
        )

        if (!jobTask || jobTask.state !== JobState.Complete) {
            throw new Error("AR generation failed")
        }
    }

    async viewArModel(templateRevisionId: string, parameters: Parameters, vertical: boolean) {
        this.creatingArSubject.next(true)

        try {
            const arModelDataObject = await this.getArModelDataObject(templateRevisionId, parameters, isIoS ? "model/vnd.usdz+zip" : "model/gltf-binary")

            if (isAndroid) {
                this.viewInArAndroid(arModelDataObject, vertical)
            } else if (isIoS) {
                this.viewInArIos(arModelDataObject)
            } else {
                this.desktopArUrl.next(assembleArUrl(EndpointUrls.AR_REDIRECT_URL, arModelDataObject.objectName, vertical))
            }
        } finally {
            this.creatingArSubject.next(false)
        }
    }

    private viewInArAndroid(gltfDataObject: ArDataObject, vertical: boolean) {
        const dataObjectURL = gltfDataObject.downloadUrl
        triggerDOMLink({
            href: generateAndroidLink(dataObjectURL, vertical),
        })
    }

    private viewInArIos(usdzDataObject: ArDataObject) {
        const dataObjectURL = usdzDataObject.downloadUrl
        // need to create a link with an <img> tag inside, as noted here: https://cwervo.com/writing/quicklook-web
        const a = document.createElement("a")
        a.href = dataObjectURL
        a.rel = "ar"
        const img = document.createElement("img")
        a.appendChild(img)
        document.body.appendChild(a)
        a.style.display = "none"
        a.click()
        a.remove()
    }
}
